import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Wo die untere Schritt-Leiste in der Vollseite beginnen muss.
 *
 * Die Leiste liegt dort `position: absolute` und richtet sich per
 * `left: var(--toc-leiste-breite)` am rechten Rand des Inhaltsverzeichnisses
 * aus (BottomBar `rootPageFull`). Bezugsrahmen von `absolute` ist aber nicht
 * das Verzeichnis, sondern der nächste positionierte Vorfahr — die
 * Fenster-Schale (FloatingWindow setzt dort `position: relative`).
 *
 * Bis 19.08.2026 stand in der Variable die KONSTANTE Sidebar-Breite (288px).
 * Das Verzeichnis beginnt aber nicht am linken Rand der Schale, sondern
 * eingerückt: die Seite hat Innenabstand und der 1200px-Container zentriert
 * sich zusätzlich automatisch. Folge: die Leiste startete zu weit links und
 * überdeckte einen Streifen des dunklen Verzeichnisses — und der Fehlbetrag
 * wanderte mit der Breite der App-Navigation (ein-/ausgeklappt), weil sich
 * damit der Freiraum links und rechts des zentrierten Containers ändert.
 *
 * Deshalb wird der Wert GEMESSEN statt gerechnet. `offsetParent` liefert per
 * Definition genau denselben Bezugsrahmen, den `position: absolute` benutzt —
 * der Hook braucht dadurch keinerlei Kenntnis der Fenster-Struktur und
 * funktioniert auch dort, wo es gar kein Fenster gibt.
 */

export type Kante = {
  left: number;
  right: number;
};

/**
 * Reine Rechenfunktion: linker Rand der Schritt-Leiste, relativ zum
 * positionierten Vorfahren.
 *
 * @param verzeichnis Rechteck des `<aside>` (Inhaltsverzeichnis) — `null`,
 *   solange keins steht (Hamburger-Modus, andere Formulare).
 * @param vorfahr Rechteck des positionierten Vorfahren (offsetParent).
 * @param borderLeftPx Linker Rahmen des Vorfahren. `getBoundingClientRect`
 *   misst inklusive Rahmen, der Bezugsrahmen von `absolute` ist aber dessen
 *   Padding-Box — ohne Abzug läge die Leiste um die Rahmenbreite daneben.
 */
export const leistenOffset = (
  verzeichnis: Kante | null | undefined,
  vorfahr: Pick<Kante, "left"> | null | undefined,
  borderLeftPx = 0,
): number | null => {
  if (!verzeichnis || !vorfahr) return null;
  // Negativ kann der Wert nur werden, wenn das Verzeichnis links aus dem
  // Vorfahren herausragt (horizontal gescrollt). Dann lieber am linken Rand
  // beginnen als die Leiste aus dem Fenster schieben.
  return Math.max(0, Math.round(verzeichnis.right - (vorfahr.left + borderLeftPx)));
};

export type TocLeistenOffset = {
  /** Callback-Ref für das `<aside>` des Inhaltsverzeichnisses. */
  ref: (el: HTMLElement | null) => void;
  /** Gemessener Offset in px — `null`, solange nichts messbar ist. */
  offset: number | null;
};

/**
 * Misst den Offset und hält ihn aktuell: ResizeObserver auf Verzeichnis UND
 * positioniertem Vorfahren plus `window resize`. Über genau diese beiden
 * Beobachter laufen auch das Ein-/Ausklappen der App-Navigation (die Schale
 * wird schmaler/breiter) und der Wechsel zwischen dauerhaftem Verzeichnis und
 * Menü-Knopf (das `<aside>` verschwindet und kommt wieder).
 *
 * Bewusst KEINE Messung nach jedem Render: die Maske hat sehr viele
 * Eingabefelder — `getBoundingClientRect` + `getComputedStyle` bei jedem
 * Tastendruck wäre Layout-Thrashing. Und der State wird nur bei echter
 * Wertänderung geschrieben, sonst entstünde eine Render-Schleife.
 */
export const useTocLeistenOffset = (): TocLeistenOffset => {
  /**
   * Das gemessene Element als State, nicht als `ref.current` — wie in
   * `useTocLayout`: das `<aside>` erscheint erst, wenn die gemessene
   * Container-Breite das dauerhafte Verzeichnis erlaubt. Ein `RefObject` löst
   * beim Belegen keinen Re-Render aus; die Effekte unten liefen dann genau
   * einmal mit `null` und hätten nie einen Beobachter angelegt.
   */
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [offset, setOffset] = useState<number | null>(null);

  const messen = useCallback(() => {
    if (!el) return;
    const vorfahr = el.offsetParent as HTMLElement | null;
    // `offsetParent` ist `null`, solange das Element (oder ein Vorfahr)
    // `display: none` trägt — genau der Fall eines MINIMIERTEN Fensters, das
    // bewusst gemountet bleibt. Dann den letzten Wert stehen lassen: ein Sprung
    // auf 0 ließe die Leiste beim Wiederherstellen kurz an den Fensterrand
    // schnellen.
    if (!vorfahr) return;
    const borderLeft = parseFloat(window.getComputedStyle(vorfahr).borderLeftWidth) || 0;
    const naechster = leistenOffset(
      el.getBoundingClientRect(),
      vorfahr.getBoundingClientRect(),
      borderLeft,
    );
    setOffset((prev) => (prev === naechster ? prev : naechster));
  }, [el]);

  // Erstmessung synchron vor dem Paint — sonst blitzt die Leiste einmal an der
  // falschen Stelle auf.
  useLayoutEffect(() => {
    messen();
  }, [messen]);

  useEffect(() => {
    if (!el) return;
    const vorfahr = el.offsetParent as HTMLElement | null;
    if (typeof ResizeObserver === "undefined") {
      // jsdom / sehr alte Browser: am Fenster hängen reicht.
      alleFenster.addEventListener("resize", messen);
      return () => alleFenster.removeEventListener("resize", messen);
    }
    const ro = new ResizeObserver(() => messen());
    ro.observe(el);
    if (vorfahr) ro.observe(vorfahr);
    alleFenster.addEventListener("resize", messen);
    return () => {
      ro.disconnect();
      alleFenster.removeEventListener("resize", messen);
    };
  }, [el, messen]);

  const ref = useCallback((next: HTMLElement | null) => {
    setEl((prev) => (prev === next ? prev : next));
  }, []);

  return { ref, offset };
};
